import React, { useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useMsal } from "@azure/msal-react";
import { orderBy } from "lodash";
import { convertDateToLocalTime } from "../../assets/utils/UtilsFunctons";
import Enums from "../../assets/JsonData/Enums";
import TablePagination from "../table/table-pagination/TablePagination";
import useUserRole from "../../hooks/useUserRole";

export const MissionSheetListTable = () => {
  const navigate = useNavigate();
  const { isAdmin, hasAccessAsDeveloper, isManager } = useUserRole();
  const { accounts } = useMsal();
  const { missionsList } = useSelector((state) => ({ ...state.missionSheet }));
  const { usersInfos } = useSelector((state) => ({ ...state.userInfos }));

  const filteredUsers = useMemo(() => {
    const users = usersInfos?.data ?? [];
    if (isManager && !isAdmin && !hasAccessAsDeveloper) {
      const oid = accounts[0]?.idTokenClaims?.oid;
      return users.filter((u) => u.operatingManagerId === oid);
    }
    return users;
  }, [usersInfos, isManager, isAdmin, hasAccessAsDeveloper, accounts]);

  // Retrouve la société d'un utilisateur à partir de son userId
  const userCompanyById = useMemo(() => {
    return new Map((usersInfos?.data ?? []).map((u) => [u.userId, u.company]));
  }, [usersInfos]);

  const getUserCompanyById = useCallback(
    (userId) => userCompanyById.get(userId) ?? null,
    [userCompanyById]
  );

  const enrichedMissionSheets = useCallback(() => {
    const data = missionsList?.data ?? [];
    const filtered =
      isManager && !isAdmin && !hasAccessAsDeveloper
        ? data.filter((m) =>
            filteredUsers.map((u) => u.userId).includes(m.userId)
          )
        : data;
    return filtered.map((mission) => ({
      ...mission,
      missionUserStatus:
        mission.endDate === null || mission.endDate > new Date() ? 0 : 1,
      company: getUserCompanyById(mission.userId)
    }));
  }, [missionsList, filteredUsers, isManager, isAdmin, hasAccessAsDeveloper, getUserCompanyById]);

  const sortedMissionSheets = useCallback(() => {
    return orderBy(
      enrichedMissionSheets(),
      ["date", "fullName"],
      ["asc", "asc"]
    );
  }, [enrichedMissionSheets]);

  const userIdDisplayValueFunc = useCallback(
    (userId) => {
      const user = filteredUsers.find((u) => u.userId === userId);
      return user ? `${user.firstName} ${user.lastName}` : null;
    },
    [filteredUsers]
  );

  const clientNameDisplayValueFunc = useCallback((clientName) => {
    return clientName;
  }, []);

  const missionsStateItemDisplayValueFunc = useCallback((missionState) => {
    return Enums.matchMissionSheetState(missionState);
  }, []);

  const missionsUserStatusDisplayValueFunc = useCallback(
    (missionUserStatus) => {
      return Enums.matchMissionSheetUserStatus(missionUserStatus);
    },
    []
  );

  const getCompaniesList = useCallback(() => {
    return [
      { value: 0, label: "Diggers" },
      { value: 1, label: "Finaura" }
    ];
  }, []);

  const companyDisplayValueFunc = useCallback((company) => {
    if (company == null) return "-";
    if (typeof company === "object" && company.label) return company.label;
    return Enums.matchCompany(company);
  }, []);

  const columns = [
    {
      Header: "Début de mission",
      accessor: "startDate",
      Cell: ({ value }) => (value ? convertDateToLocalTime(value) : "")
    },
    {
      Header: "Statut de la fiche",
      accessor: "lastMissionSheetState",
      Cell: ({ value }) => missionsStateItemDisplayValueFunc(value)
    },
    {
      Header: "Collaborateur",
      accessor: "consultantFullName"
    },
    {
      Header: "Société",
      accessor: "company",
      Cell: ({ value }) => Enums.matchCompany(value)
    },
    {
      Header: "Mission",
      accessor: "consultantRole"
    },
    {
      Header: "Client",
      accessor: "clientName",
      Cell: ({ value }) => clientNameDisplayValueFunc(value)
    },
    {
      Header: "Période",
      accessor: "period",
      Cell: ({ row }) => (
        <span>
          {row?.original?.startDate
            ? convertDateToLocalTime(row.original.startDate)
            : ""}
          <span>&nbsp;-&nbsp;</span>
          {row?.original?.endDate
            ? convertDateToLocalTime(row.original.endDate)
            : "..."}
        </span>
      )
    },
    {
      Header: "Statut mission",
      accessor: "missionUserStatus",
      Cell: ({ value }) => missionsUserStatusDisplayValueFunc(value)
    }
  ];

  const getDefaultValueData = useCallback((data) => {
    if (data) {
      return data.filter((missionSheet) => missionSheet.state === 2);
    }
    return null;
  }, []);

  /* eslint @typescript-eslint/no-empty-function: "off", @typescript-eslint/no-unused-vars: "off" */
  const tableHooks = (hooks) => {};

  const handleRowClicked = useCallback(
    (row) => {
      navigate("/MissionSheetDetailPage/" + row.original.missionId, {
        state: { consultantName: row.original.consultantFullName }
      });
    },
    [navigate]
  );

  return (
    <div>
      {missionsList?.data ? (
        <TablePagination
          data={sortedMissionSheets() || []}
          columns={columns}
          tableHooks={tableHooks}
          refresh={
            missionsList?.data ||
            isAdmin ||
            !hasAccessAsDeveloper ||
            userIdDisplayValueFunc
          }
          type="default"
          enableFilter={true}
          hasDefaultValue={true}
          getDefaultValueData={getDefaultValueData}
          handleRowClicked={handleRowClicked}>
          <element
            key="userId"
            filterKey="userId"
            className="cell"
            casesensitive="true"
            showsearch="true"
            itemDisplayValueFunc={userIdDisplayValueFunc}>
            Collab.
          </element>
          <element
            key="company"
            filterKey="company"
            className="cell"
            casesensitive="true"
            showsearch="true"
            data={getCompaniesList()}
            itemDisplayValueFunc={companyDisplayValueFunc}>
            Société
          </element>
          <element
            key="lastMissionSheetState"
            filterKey="lastMissionSheetState"
            className="cell"
            casesensitive="true"
            showsearch="true"
            isMulti
            itemDisplayValueFunc={missionsStateItemDisplayValueFunc}
            hasDefaultValue={true}
            defaultValue={0}>
            Statut de la fiche
          </element>
          <element
            key="missionUserStatus"
            filterKey="missionUserStatus"
            className="cell"
            casesensitive="true"
            showsearch="true"
            isMulti
            itemDisplayValueFunc={missionsUserStatusDisplayValueFunc}
            hasDefaultValue={true}
            defaultValue={0}>
            Statut de la mission
          </element>
          <element
            key="clientName"
            filterKey="clientName"
            className="cell"
            casesensitive="true"
            showsearch="true"
            itemDisplayValueFunc={clientNameDisplayValueFunc}>
            Client
          </element>
        </TablePagination>
      ) : null}
    </div>
  );
};
